import { Fragment, useMemo, useState, type ReactNode } from "react";
import { BookOpen, CornerDownRight, Plus, Search, X } from "lucide-react";
import type { Account } from "../../services/financialService";
import { Pager, paginate } from "../Pager";
import { ExportButtons, type ExportFormat } from "../ExportButtons";
import { exportAsCsv, exportAsExcel, exportAsPdf } from "../../utils/exportTable";
import { AccountDetailsModal } from "./AccountDetailsModal";
import {
  ACCOUNT_STANDINGS,
  ACCOUNT_TYPES,
  accountPurpose,
  accountStanding,
  activeChartFilterCount,
  CHART_PAGE_SIZES,
  chartExportColumns,
  chartRows,
  countByType,
  EMPTY_CHART_FILTERS,
  filterChartRows,
  PURPOSE_LABELS,
  TYPE_DOTS,
  type AccountStanding,
  type AccountType,
  type ChartFilters,
} from "./chartOfAccounts";

const COLUMNS = 9;

/** The ledger as one reading-order table: each type together, main accounts
 *  with their subaccounts tucked under them, and what each can still do. */
export default function ChartOfAccountsView({
  accounts,
  isLoading,
  canCreate,
  onNew,
  actions,
}: {
  accounts?: Account[];
  isLoading: boolean;
  canCreate: boolean;
  onNew: (type?: AccountType) => void;
  /** The row's own buttons -- edit, submit, delete -- decided by the page. */
  actions: (account: Account) => ReactNode;
}) {
  const [filters, setFilters] = useState(EMPTY_CHART_FILTERS);
  const [pageRequested, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(CHART_PAGE_SIZES[1]);
  const [viewingId, setViewingId] = useState<number | null>(null);

  const rows = useMemo(() => chartRows(accounts ?? []), [accounts]);
  const typeCounts = countByType(filterChartRows(rows, filters, { ignoreType: true }));
  const visible = filterChartRows(rows, filters);
  const activeFilters = activeChartFilterCount(filters);
  const { rows: paged, first, page, pageCount } = paginate(visible, pageRequested, pageSize);
  const viewing = rows.find((row) => row.account.id === viewingId) ?? null;
  const exportChart = (format: ExportFormat) => {
    const columns = chartExportColumns();
    if (format === "csv") exportAsCsv("chart-of-accounts.csv", columns, visible);
    if (format === "excel") exportAsExcel("chart-of-accounts.xls", "Chart of Accounts", columns, visible);
    if (format === "pdf") exportAsPdf("Chart of Accounts", columns, visible);
  };
  const update = (changes: Partial<ChartFilters>) => {
    setFilters((current) => ({ ...current, ...changes }));
    setPage(1);
  };

  if (isLoading) {
    return (
      <div className="card card-content space-y-2 pt-6">
        {Array.from({ length: 8 }).map((_, index) => (
          <div key={index} className="h-7 animate-pulse rounded bg-gray-100" />
        ))}
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="card overflow-hidden">
        <div className="border-b border-gray-100 bg-gradient-to-br from-primary-50 via-white to-white px-6 py-8 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-600 text-white shadow-sm">
            <BookOpen className="h-7 w-7" />
          </span>
          <h2 className="mt-4 text-lg font-semibold text-gray-900">Start the school's chart of accounts</h2>
          <p className="mx-auto mt-1 max-w-xl text-sm text-gray-500">
            Every receipt, bill and payroll run lands in one of these accounts. Add the accounts the school
            keeps — each is sent for approval before anything can post to it.
          </p>
          {canCreate && (
            <button type="button" className="btn btn-primary mt-5 gap-2" onClick={() => onNew()}>
              <Plus className="h-4 w-4" /> Create the first account
            </button>
          )}
        </div>
        <div className="grid gap-px bg-gray-100 sm:grid-cols-5">
          {ACCOUNT_TYPES.map((entry) => (
            <button
              key={entry.type}
              type="button"
              disabled={!canCreate}
              onClick={() => onNew(entry.type)}
              className="group bg-white p-4 text-left transition-colors enabled:hover:bg-primary-50/50 disabled:cursor-default"
            >
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-700">
                <span className={`h-2 w-2 rounded-full ${entry.dot}`} /> {entry.plural}
              </span>
              <p className="mt-1.5 text-xs leading-5 text-gray-500">{entry.hint}</p>
              {canCreate && (
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary-700 opacity-0 transition-opacity group-hover:opacity-100">
                  <Plus className="h-3 w-3" /> Add {entry.label.toLowerCase()} account
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header pb-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <BookOpen className="h-5 w-5" /> Chart of accounts
            </h2>
            <p className="text-sm text-gray-500">
              The ledger in reading order: each main account with its subaccounts beneath it.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <ExportButtons onExport={exportChart} disabled={!visible.length} what="the filtered chart" />
            {canCreate && (
              <button type="button" className="btn btn-primary h-8 min-h-0 gap-1.5 px-3 py-1 text-xs" onClick={() => onNew(filters.type || undefined)}>
                <Plus className="h-3.5 w-3.5" /> New account
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="card-content space-y-4">
        <div className="filter-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div role="group" aria-label="Account type" className="segmented">
              {[{ type: "" as const, plural: "All" }, ...ACCOUNT_TYPES].map((entry) => (
                <button
                  key={entry.type || "all"}
                  type="button"
                  aria-pressed={filters.type === entry.type}
                  onClick={() => update({ type: entry.type })}
                  className="segmented-option inline-flex items-center"
                >
                  {entry.type && <span className={`mr-1.5 h-1.5 w-1.5 rounded-full ${TYPE_DOTS[entry.type]}`} />}
                  {entry.plural}
                  <span className="ml-1.5 tabular-nums opacity-70">{typeCounts[entry.type] ?? 0}</span>
                </button>
              ))}
            </div>
            {activeFilters > 0 && (
              <button
                type="button"
                onClick={() => update(EMPTY_CHART_FILTERS)}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
              >
                <X className="h-3.5 w-3.5" /> Clear {activeFilters} {activeFilters === 1 ? "filter" : "filters"}
              </button>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(16rem,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
            <label className="filter-field sm:col-span-2 lg:col-span-1">
              Search
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="search"
                  value={filters.search}
                  onChange={(event) => update({ search: event.target.value })}
                  className="input-compact pl-8"
                  placeholder="Code, name, description or parent account"
                />
              </div>
            </label>
            <label className="filter-field">
              Standing
              <select
                value={filters.standing}
                onChange={(event) => update({ standing: event.target.value as AccountStanding | "" })}
                className="input-compact"
              >
                <option value="">Any standing</option>
                {Object.entries(ACCOUNT_STANDINGS).map(([value, { label }]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
            <label className="filter-field">
              Purpose
              <select
                value={filters.purpose}
                onChange={(event) => update({ purpose: event.target.value as ChartFilters["purpose"] })}
                className="input-compact"
              >
                <option value="">Any purpose</option>
                <option value="posting">Posting accounts</option>
                <option value="summary">Summary only</option>
                <option value="control">Control accounts</option>
              </select>
            </label>
          </div>
        </div>

        {!visible.length ? (
          <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
            No accounts match these filters.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="sheet-wrap">
              <table className="sheet">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Account</th>
                    <th>Type</th>
                    <th>Purpose</th>
                    <th>Normal</th>
                    <th>Statement line</th>
                    <th className="text-right">Postings</th>
                    <th>Standing</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map(({ account, depth, path, children }, index) => {
                    const previous = paged[index - 1]?.account.account_type;
                    const typeEntry = ACCOUNT_TYPES.find((entry) => entry.type === account.account_type);
                    const standing = ACCOUNT_STANDINGS[accountStanding(account)];
                    const showGroup = !filters.type && previous !== account.account_type;
                    return (
                      <Fragment key={account.id}>
                        {showGroup && (
                          <tr className="bg-gray-50 hover:bg-gray-50">
                            <td colSpan={COLUMNS} className="py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                              <span className="inline-flex items-center gap-1.5">
                                <span className={`h-2 w-2 rounded-full ${TYPE_DOTS[account.account_type]}`} />
                                {typeEntry?.plural}
                                <span className="font-normal normal-case tracking-normal text-gray-400">
                                  · {typeCounts[account.account_type]} account{typeCounts[account.account_type] === 1 ? "" : "s"}
                                </span>
                              </span>
                            </td>
                          </tr>
                        )}
                        <tr className="cursor-pointer bg-white" onClick={() => setViewingId(account.id)}>
                          <td className="font-mono text-xs text-gray-700">{account.code}</td>
                          <td className="max-w-[22rem]">
                            <div className="flex items-center gap-1.5" style={{ paddingLeft: `${depth * 1.25}rem` }}>
                              {depth > 0 && <CornerDownRight className="h-3.5 w-3.5 shrink-0 text-gray-300" />}
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setViewingId(account.id);
                                }}
                                className={`truncate text-left hover:text-primary-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${depth ? "text-gray-800" : "font-semibold text-gray-900"}`}
                                title={[...path, account.name].join(" › ")}
                              >
                                {account.name}
                              </button>
                              {children > 0 && (
                                <span className="rounded bg-gray-100 px-1.5 text-[10px] font-medium text-gray-500">
                                  {children} sub
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className="inline-flex items-center gap-1.5 text-xs text-gray-700">
                              <span className={`h-1.5 w-1.5 rounded-full ${TYPE_DOTS[account.account_type]}`} />
                              {typeEntry?.label}
                            </span>
                          </td>
                          <td className="text-xs text-gray-600">
                            {PURPOSE_LABELS[accountPurpose(account)]}
                            {account.is_contra_account && <span className="ml-1 text-amber-700">· contra</span>}
                          </td>
                          <td className="text-xs uppercase text-gray-500">{account.normal_balance === "debit" ? "Dr" : "Cr"}</td>
                          <td className="max-w-[12rem] truncate text-xs text-gray-600" title={account.statement_section_name || undefined}>
                            {account.statement_category_name || (depth ? <span className="text-gray-300">via parent</span> : <span className="text-gray-300">—</span>)}
                          </td>
                          <td className="text-right tabular-nums text-gray-600">
                            {account.transaction_count || <span className="text-gray-300">0</span>}
                          </td>
                          <td title={account.return_reason || undefined}>
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700">
                              <span className={`h-1.5 w-1.5 rounded-full ${standing.dot}`} />
                              {standing.label}
                            </span>
                          </td>
                          <td className="text-right" onClick={(event) => event.stopPropagation()}>
                            <div className="inline-flex items-center gap-1">{actions(account)}</div>
                          </td>
                        </tr>
                        {account.workflow_status === "returned" && account.return_reason && (
                          <tr className="bg-orange-50/60 hover:bg-orange-50/60">
                            <td />
                            <td colSpan={COLUMNS - 1} className="whitespace-normal text-xs text-orange-800">
                              Sent back: {account.return_reason}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pager
              first={first}
              shown={paged.length}
              total={visible.length}
              noun={visible.length === 1 ? "account" : "accounts"}
              page={page}
              pageCount={pageCount}
              onPage={setPage}
              pageSize={pageSize}
              pageSizes={CHART_PAGE_SIZES}
              onPageSize={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>
      <AccountDetailsModal
        row={viewing}
        subaccounts={(accounts ?? []).filter((account) => account.parent === viewingId && account.id !== viewingId)}
        onClose={() => setViewingId(null)}
        onOpenAccount={(account) => setViewingId(account.id)}
        actions={viewing && (
          // Edit and delete open their own dialogs, so this one steps aside first.
          <div className="flex flex-wrap items-center gap-1" onClickCapture={() => setViewingId(null)}>
            {actions(viewing.account)}
          </div>
        )}
      />
    </div>
  );
}
